import { useState, useEffect, useCallback, useMemo } from 'react';
import { PanelLeftOpen } from 'lucide-react';
import { DataLibraryHeader } from './DataLibraryHeader';
import { DataLibrarySidePanel } from './DataLibrarySidePanel';
import { DataLibraryTabs } from './DataLibraryTabs';
import { DataLibraryParticipantsTab } from './participants/DataLibraryParticipantsTab';
import type { ParticipantsTabConfig } from './participants/useDataLibraryParticipants';
import { DataLibrarySurveyTab } from './survey-tabs/DataLibrarySurveyTab';
import { DataLibraryCoachSurveyTab } from './survey-tabs/DataLibraryCoachSurveyTab';
import { DataLibraryCoachMidVsEndTab } from './survey-tabs/DataLibraryCoachMidVsEndTab';
import { DataLibraryCoachPostSessionTab } from './coach-post-session/DataLibraryCoachPostSessionTab';
import { DataLibraryAttendanceTab } from './attendance/DataLibraryAttendanceTab';
import { DataLibrarySessionFeedbackTab } from './survey-tabs/DataLibrarySessionFeedbackTab';
import { DataLibrarySessionsTab } from './sessions/DataLibrarySessionsTab';
import { DataLibraryOneOnOnesTab } from './one-on-ones/DataLibraryOneOnOnesTab';
import { DataLibraryOneOnOneHistoryTab } from './one-on-one-history/DataLibraryOneOnOneHistoryTab';
import { Text, Center, Drawer } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { dataLibraryApi } from '../../services/api/dataLibrary';
import type { DataLibraryCohortGroup } from '../../types';
import type { GroupState } from '../common/GroupPanel/GroupPanel';
import type { ActiveFilterRow } from '../common/FilterPanel/FilterPanel';
import { ParticipantDetailProvider } from './components/ParticipantEmailPill';
import classes from './DataLibraryDrawer.module.css';
import { ColumnResizeHandle } from '../common/ConfigurableTable/ConfigurableTable';
import { useAuthStore } from '../../stores/auth';
import { DataLibraryTabContext } from './useDataLibraryTableLayout';
import {
  STORAGE_PREFIX,
  SIDE_PANEL_DEFAULT_WIDTH,
  SIDE_PANEL_MIN_WIDTH,
  SIDE_PANEL_MAX_WIDTH,
  clampWidth,
  parseSidePanelWidth,
  readStorage,
  writeStorage,
} from './columnState';

interface DataLibraryLayoutProps {
  onClose?: () => void;
  defaultCohortId?: string;
  defaultTab?: string;
  onCohortChange?: (cohortIds: string[]) => void;
  participantsConfig?: ParticipantsTabConfig;
  /** MED-661: Initial group-by state for the active tab */
  defaultGroup?: GroupState | null;
  /** MED-661: Initial filters for the active tab */
  defaultFilters?: ActiveFilterRow[];
  /** MED-764: Auto-open participant detail modal for this participant on mount */
  defaultParticipantId?: string;
  /** MED-1346: the "1:1 history" tab opens filtered to this participant (user id) */
  historyParticipantId?: string;
  /** Tells a parent Drawer that the phone cohort panel is open, so its Escape closes only the panel */
  onCohortPanelOverlayChange?: (open: boolean) => void;
}

export function DataLibraryLayout({
  onClose,
  defaultCohortId,
  defaultTab,
  onCohortChange,
  participantsConfig,
  defaultGroup,
  defaultFilters,
  defaultParticipantId,
  historyParticipantId,
  onCohortPanelOverlayChange,
}: DataLibraryLayoutProps) {
  const [activeTab, setActiveTab] = useState(defaultTab || 'participants');
  // MED-1346: the link's participant filter applies on the first history tab mount only.
  const [pendingHistoryParticipant, setPendingHistoryParticipant] = useState(historyParticipantId);
  useEffect(() => {
    if (activeTab === 'one_on_one_history' && pendingHistoryParticipant) setPendingHistoryParticipant(undefined);
  }, [activeTab, pendingHistoryParticipant]);
  // The Data Library page passes its ?cohort= value, which can hold several ids.
  // Read it once: each cohort click rewrites the URL, and a new value here would
  // load the cohort list again.
  const [initialCohortIds] = useState(() => defaultCohortId?.split(',').filter(Boolean) ?? []);
  const [selectedCohortIds, setSelectedCohortIds] = useState<string[]>(initialCohortIds);
  const [cohortGroups, setCohortGroups] = useState<DataLibraryCohortGroup[]>([]);
  const [loadingCohorts, setLoadingCohorts] = useState(true);
  const [sidePanelCollapsed, setSidePanelCollapsed] = useState(false);
  // On a phone the cohort panel starts collapsed and opens as an overlay, with no drag handle.
  const isPhone = useMediaQuery('(max-width: 48em)', false, { getInitialValueInEffect: false });
  const [phonePanelOpen, setPhonePanelOpen] = useState(false);
  // Reset during render, so a later phone width never opens the overlay by itself.
  if (!isPhone && phonePanelOpen) setPhonePanelOpen(false);
  const phoneOverlayOpen = isPhone && phonePanelOpen;
  useEffect(() => {
    onCohortPanelOverlayChange?.(phoneOverlayOpen);
  }, [phoneOverlayOpen, onCohortPanelOverlayChange]);

  // MDF-223: the cohort panel width, set by drag and saved per user.
  const userId = useAuthStore(s => s.user?.id);
  const sidePanelKey = userId ? `${STORAGE_PREFIX}${userId}.sidePanelWidth` : null;
  const [sidePanelWidth, setSidePanelWidth] = useState(() =>
    parseSidePanelWidth(sidePanelKey ? readStorage(sidePanelKey) : null),
  );
  const saveSidePanelWidth = useCallback(
    (width: number) => {
      const next = clampWidth(width, SIDE_PANEL_MIN_WIDTH, SIDE_PANEL_MAX_WIDTH);
      setSidePanelWidth(next);
      if (sidePanelKey) writeStorage(sidePanelKey, String(next));
    },
    [sidePanelKey],
  );

  // Per-tab group-by state — ensures each survey tab has independent, preserved group-by selection
  const [groupByPerTab, setGroupByPerTab] = useState<Record<string, GroupState | null>>({
    participant_mid_vs_end: [{ field: 'participant', order: 'alphabetical' }],
  });

  const handleGroupChange = useCallback((tab: string, group: GroupState | null) => {
    setGroupByPerTab(prev => ({ ...prev, [tab]: group }));
  }, []);

  const surveyTabGroupProps = useMemo(() => ({
    participant_midpoint: {
      activeGroup: groupByPerTab['participant_midpoint'] ?? null,
      onGroupChange: (g: GroupState | null) => handleGroupChange('participant_midpoint', g),
    },
    participant_endpoint: {
      activeGroup: groupByPerTab['participant_endpoint'] ?? null,
      onGroupChange: (g: GroupState | null) => handleGroupChange('participant_endpoint', g),
    },
    participant_mid_vs_end: {
      activeGroup: 'participant_mid_vs_end' in groupByPerTab
        ? groupByPerTab['participant_mid_vs_end']
        : [{ field: 'participant', order: 'alphabetical' as const }],
      onGroupChange: (g: GroupState | null) => handleGroupChange('participant_mid_vs_end', g),
    },
    coach_midpoint: {
      activeGroup: groupByPerTab['coach_midpoint'] ?? null,
      onGroupChange: (g: GroupState | null) => handleGroupChange('coach_midpoint', g),
    },
    coach_endpoint: {
      activeGroup: groupByPerTab['coach_endpoint'] ?? null,
      onGroupChange: (g: GroupState | null) => handleGroupChange('coach_endpoint', g),
    },
    coach_mid_vs_end: {
      activeGroup: 'coach_mid_vs_end' in groupByPerTab
        ? groupByPerTab['coach_mid_vs_end']
        : [{ field: 'full_name', order: 'alphabetical' as const }],
      onGroupChange: (g: GroupState | null) => handleGroupChange('coach_mid_vs_end', g),
    },
    coach_post_session: {
      activeGroup: 'coach_post_session' in groupByPerTab
        ? groupByPerTab['coach_post_session']
        : [{ field: 'coach_name', order: 'alphabetical' as const }],
      onGroupChange: (g: GroupState | null) => handleGroupChange('coach_post_session', g),
    },
    participant_session_feedback: {
      activeGroup: groupByPerTab['participant_session_feedback'] ?? null,
      onGroupChange: (g: GroupState | null) => handleGroupChange('participant_session_feedback', g),
    },
  }), [groupByPerTab, handleGroupChange]);

  const fetchCohorts = useCallback(async () => {
    try {
      setLoadingCohorts(true);
      const data = await dataLibraryApi.getCohorts();
      setCohortGroups(data);

      // Auto-select default cohort if provided and valid
      if (initialCohortIds.length > 0) {
        setSelectedCohortIds(prev => {
          if (prev.length > 0) return prev;
          const allCohortIds = data.flatMap(g => g.cohorts.map(c => c.id));
          return initialCohortIds.filter(id => allCohortIds.includes(id));
        });
      }
    } finally {
      setLoadingCohorts(false);
    }
  }, [initialCohortIds]);

  useEffect(() => {
    fetchCohorts();
  }, [fetchCohorts]);

  // onCohortChange updates the URL, so it runs here and not in a state updater,
  // which React calls during render.
  const changeCohorts = useCallback(
    (next: string[]) => {
      setSelectedCohortIds(next);
      onCohortChange?.(next);
    },
    [onCohortChange],
  );

  const handleCohortToggle = useCallback(
    (cohortId: string) =>
      changeCohorts(
        selectedCohortIds.includes(cohortId)
          ? selectedCohortIds.filter(id => id !== cohortId)
          : [...selectedCohortIds, cohortId],
      ),
    [selectedCohortIds, changeCohorts],
  );

  const handleSelectAll = useCallback(
    (partnerCohortIds: string[]) => {
      const allSelected = partnerCohortIds.every(id => selectedCohortIds.includes(id));
      changeCohorts(
        allSelected
          ? selectedCohortIds.filter(id => !partnerCohortIds.includes(id))
          : [...new Set([...selectedCohortIds, ...partnerCohortIds])],
      );
    },
    [selectedCohortIds, changeCohorts],
  );

  return (
    <ParticipantDetailProvider initialParticipantId={defaultParticipantId} initialCohortId={defaultCohortId}>
    <DataLibraryTabContext.Provider value={activeTab}>
    <div className={classes.layout}>
      <DataLibraryHeader onClose={onClose} />
      <div className={classes.mainContent}>
        {sidePanelCollapsed || isPhone ? (
          <div className={classes.sidePanelCollapsedBar}>
            <button
              className={classes.collapseButton}
              onClick={() => (isPhone ? setPhonePanelOpen(true) : setSidePanelCollapsed(false))}
              title="Expand sidebar"
            >
              <PanelLeftOpen size={18} />
            </button>
          </div>
        ) : (
          <div className={classes.sidePanelResizable} style={{ width: sidePanelWidth }}>
            <DataLibrarySidePanel
              cohortGroups={cohortGroups}
              selectedCohortIds={selectedCohortIds}
              onCohortToggle={handleCohortToggle}
              onSelectAll={handleSelectAll}
              loading={loadingCohorts}
              onCollapse={() => setSidePanelCollapsed(true)}
            />
            <ColumnResizeHandle
              columnId="sidePanel"
              ariaLabel="Resize cohort panel"
              width={sidePanelWidth}
              onResize={(_, width) => saveSidePanelWidth(width)}
              onReset={() => saveSidePanelWidth(SIDE_PANEL_DEFAULT_WIDTH)}
            />
          </div>
        )}
        <Drawer.Root opened={phoneOverlayOpen} onClose={() => setPhonePanelOpen(false)} size="100%">
          <Drawer.Overlay />
          <Drawer.Content aria-label="Programs and cohorts">
            <Drawer.Body p={0} h="100%" display="flex">
              <DataLibrarySidePanel
                cohortGroups={cohortGroups}
                selectedCohortIds={selectedCohortIds}
                onCohortToggle={handleCohortToggle}
                onSelectAll={handleSelectAll}
                loading={loadingCohorts}
                onCollapse={() => setPhonePanelOpen(false)}
              />
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Root>
        <div className={classes.contentArea}>
          {selectedCohortIds.length === 0 ? (
            <Center style={{ flex: 1 }}>
              <Text c="dimmed" size="lg">
                Select a cohort to review data
              </Text>
            </Center>
          ) : (
            <>
              <div className={classes.tabsContainer}>
                <DataLibraryTabs activeTab={activeTab} onTabChange={setActiveTab} />
              </div>
              <div className={classes.tabContent}>
                {activeTab === 'participants' ? (
                  <DataLibraryParticipantsTab selectedCohortIds={selectedCohortIds} initialConfig={participantsConfig} />
                ) : activeTab === 'attendance' ? (
                  <DataLibraryAttendanceTab selectedCohortIds={selectedCohortIds} initialGroup={defaultGroup} initialFilters={defaultFilters} />
                ) : activeTab === 'participant_midpoint' ? (
                  <DataLibrarySurveyTab
                    selectedCohortIds={selectedCohortIds}
                    formTypes={['mid_program']}
                    emptyMessage="No midpoint survey responses yet"
                    noConfigMessage="No midpoint survey configured for this cohort"
                    entityLabel="midpoint responses"
                    initialFilters={defaultFilters}
                    {...surveyTabGroupProps.participant_midpoint}
                  />
                ) : activeTab === 'participant_endpoint' ? (
                  <DataLibrarySurveyTab
                    selectedCohortIds={selectedCohortIds}
                    formTypes={['final_survey']}
                    emptyMessage="No endpoint survey responses yet"
                    noConfigMessage="No endpoint survey configured for this cohort"
                    entityLabel="endpoint responses"
                    initialFilters={defaultFilters}
                    {...surveyTabGroupProps.participant_endpoint}
                  />
                ) : activeTab === 'participant_mid_vs_end' ? (
                  <DataLibrarySurveyTab
                    selectedCohortIds={selectedCohortIds}
                    formTypes={['mid_program', 'final_survey']}
                    emptyMessage="No survey responses to compare"
                    noConfigMessage="No surveys configured for this cohort"
                    entityLabel="survey responses"
                    initialFilters={defaultFilters}
                    {...surveyTabGroupProps.participant_mid_vs_end}
                  />
                ) : activeTab === 'coach_midpoint' ? (
                  <DataLibraryCoachSurveyTab
                    selectedCohortIds={selectedCohortIds}
                    surveyType="midpoint"
                    emptyMessage="No coach midpoint feedback yet"
                    noConfigMessage="No coach survey configured for this cohort"
                    {...surveyTabGroupProps.coach_midpoint}
                  />
                ) : activeTab === 'coach_endpoint' ? (
                  <DataLibraryCoachSurveyTab
                    selectedCohortIds={selectedCohortIds}
                    surveyType="endpoint"
                    emptyMessage="No coach endpoint feedback yet"
                    noConfigMessage="No coach survey configured for this cohort"
                    {...surveyTabGroupProps.coach_endpoint}
                  />
                ) : activeTab === 'coach_mid_vs_end' ? (
                  <DataLibraryCoachMidVsEndTab
                    selectedCohortIds={selectedCohortIds}
                    {...surveyTabGroupProps.coach_mid_vs_end}
                  />
                ) : activeTab === 'coach_post_session' ? (
                  <DataLibraryCoachPostSessionTab
                    selectedCohortIds={selectedCohortIds}
                    {...surveyTabGroupProps.coach_post_session}
                  />
                ) : activeTab === 'participant_session_feedback' ? (
                  <DataLibrarySessionFeedbackTab
                    selectedCohortIds={selectedCohortIds}
                    {...surveyTabGroupProps.participant_session_feedback}
                  />
                ) : activeTab === 'sessions' ? (
                  <DataLibrarySessionsTab selectedCohortIds={selectedCohortIds} initialFilters={defaultFilters} />
                ) : activeTab === 'one_on_ones' ? (
                  <DataLibraryOneOnOnesTab selectedCohortIds={selectedCohortIds} />
                ) : activeTab === 'one_on_one_history' ? (
                  <DataLibraryOneOnOneHistoryTab
                    selectedCohortIds={selectedCohortIds}
                    initialFilters={
                      pendingHistoryParticipant ? [{ key: 'participant', values: [pendingHistoryParticipant] }] : undefined
                    }
                  />
                ) : (
                  <Center h={300}>
                    <Text c="dimmed" size="lg">
                      Feature Coming Soon
                    </Text>
                  </Center>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
    </DataLibraryTabContext.Provider>
    </ParticipantDetailProvider>
  );
}
